/**
 * Resolves effective participants for a session (cycle + track + session, users + groups).
 * Used for participant counts, facilitator picker, and session participation UI.
 */

import { useState, useEffect, useMemo } from 'react';
import { resolveEffectiveParticipantsForSession, type EffectiveParticipant } from '../lib/effectiveParticipantResolver';
import { getMemberships } from '../lib/groupsService';
import { useCycleParticipantsStore } from '../stores/prioritization/cycleParticipantsStore';
import { useCycleParticipantGroupsStore } from '../stores/prioritization/cycleParticipantGroupsStore';
import { useTrackParticipantsStore } from '../stores/prioritization/trackParticipantsStore';
import { useTrackParticipantGroupsStore } from '../stores/prioritization/trackParticipantGroupsStore';
import { useSessionParticipantsStore } from '../stores/prioritization/sessionParticipantsStore';
import { useSessionParticipantGroupsStore } from '../stores/prioritization/sessionParticipantGroupsStore';
import type { PrioritizationSession } from '../types/prioritization/session';

export function useEffectiveSessionParticipants(session: PrioritizationSession | null) {
  const [effective, setEffective] = useState<EffectiveParticipant[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSessionParticipants = useSessionParticipantsStore(s => s.fetchParticipantsForSession);
  const allParticipants = useSessionParticipantsStore(s => s.participants);
  const storeSessionDirectIds = useMemo(() => {
    const id = session?.id ?? '';
    return allParticipants
      .filter(p => p.sessionId === id && p.status === 'active')
      .map(p => p.userId);
  }, [allParticipants, session?.id]);

  const cycleParticipants = useCycleParticipantsStore(s => s.participants);
  const cycleGroups = useCycleParticipantGroupsStore(s => s.participantGroups);
  const trackParticipants = useTrackParticipantsStore(s => s.participants);
  const trackGroups = useTrackParticipantGroupsStore(s => s.participantGroups);
  const sessionGroups = useSessionParticipantGroupsStore(s => s.participantGroups);

  const cycleId = session?.cycleId;
  const trackId = session?.trackId;

  useEffect(() => {
    if (session?.id) fetchSessionParticipants(session.id);
  }, [session?.id, fetchSessionParticipants]);

  const sessionDirectIds = useMemo(() => {
    if (!session?.id) return [];
    return storeSessionDirectIds.length > 0 ? storeSessionDirectIds : (session.participantIds ?? []);
  }, [session?.id, session?.participantIds, storeSessionDirectIds]);

  const cycleDirect = useMemo(() => {
    if (!cycleId) return [];
    return cycleParticipants
      .filter(p => p.cycleId === cycleId && p.status === 'active')
      .map(p => ({ userId: p.userId }));
  }, [cycleId, cycleParticipants]);

  const cycleGroupList = useMemo(() => {
    if (!cycleId) return [];
    return cycleGroups
      .filter(g => g.cycleId === cycleId && g.status === 'active')
      .map(g => ({ groupId: g.groupId }));
  }, [cycleId, cycleGroups]);

  const trackDirect = useMemo(() => {
    if (!trackId) return [];
    return trackParticipants
      .filter(p => p.trackId === trackId && p.status === 'active')
      .map(p => ({ userId: p.userId }));
  }, [trackId, trackParticipants]);

  const trackGroupList = useMemo(() => {
    if (!trackId) return [];
    return trackGroups
      .filter(g => g.trackId === trackId && g.status === 'active')
      .map(g => ({ groupId: g.groupId }));
  }, [trackId, trackGroups]);

  const sessionGroupList = useMemo(() => {
    if (!session?.id) return [];
    return sessionGroups
      .filter(g => g.sessionId === session.id && g.status === 'active')
      .map(g => ({ groupId: g.groupId }));
  }, [session?.id, sessionGroups]);

  useEffect(() => {
    if (!session?.id) {
      setEffective([]);
      setLoading(false);
      return;
    }

    // Session App / isolated / app-linked sessions often have no cycle+track.
    // Previously this returned [] → UI showed 0 participants even when the
    // session roster had the facilitator (and later joiners). Use session-direct
    // ids as the effective set in that case.
    if (!session.cycleId || !session.trackId) {
      setEffective(
        sessionDirectIds.map((userId) => ({
          userId,
          provenance: 'direct' as const,
          viaGroupIds: [] as string[],
          sources: ['session_direct' as const],
        })),
      );
      setLoading(false);
      return;
    }

    setLoading(true);
    resolveEffectiveParticipantsForSession({
      cycleDirectUsers: cycleDirect,
      cycleParticipantGroups: cycleGroupList,
      trackDirectUsers: trackDirect,
      trackParticipantGroups: trackGroupList,
      sessionDirectUserIds: sessionDirectIds,
      sessionParticipantGroups: sessionGroupList,
      getMemberUserIdsForGroup: async (groupId: string) => {
        const memberships = await getMemberships({ groupId });
        return [...new Set(memberships.map(m => m.userId).filter(Boolean))];
      },
    })
      .then(setEffective)
      .catch(() => setEffective([]))
      .finally(() => setLoading(false));
  }, [
    session?.id,
    session?.cycleId,
    session?.trackId,
    cycleDirect,
    cycleGroupList,
    trackDirect,
    trackGroupList,
    sessionDirectIds,
    sessionGroupList,
  ]);

  const effectiveUserIds = useMemo(() => effective.map(e => e.userId), [effective]);
  return { effective, effectiveUserIds, loading };
}
